/**
 * VaultFilePickerDialog — the long-standing picker API, now a thin preset over
 * the host Open dialog (`components/files/file-dialog/OpenFileDialog`).
 *
 * Every "pick a file" surface that predates the file dialogs (chat context,
 * Travel attachments, Social Media's media picker, Image Editor, the Icon
 * dev-console picker, Document Sign, Banking, Fias Transfer, Email compose)
 * keeps calling it with the same props and gets the same `VaultPickerResult`
 * — a durable `documentId` plus a fresh 1-hour `downloadUrl` — while the
 * browsing itself (server search, paging, keyboard, preview, Documents /
 * Arche Saves / Recent) is the one shared implementation.
 */
import { useCallback, useMemo } from 'react';
import { api, type VaultDocumentDto, type VaultFileDialogDocumentDto } from '@/lib/api';
import type { VaultFileKind } from '@/lib/vault-file-kinds';
import { OpenFileDialog } from '@/components/files/file-dialog/OpenFileDialog';
import type { FileAccept } from '@/components/files/file-dialog/useFileBrowser';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface VaultPickerResult {
  /** Vault document id — the durable handle. */
  documentId: string;
  /** The workspace the document is in (set by the picker; absent on results
   *  a surface builds itself). */
  holderId?: string;
  fileName: string;
  mimeType: string | null;
  sizeBytes: number;
  /** Vault s3Key — usable as a stable ref for downstream API calls. */
  s3Key: string;
  /** Fresh 1-hour signed download URL for `<img src>`, `<audio src>`, fetch(). */
  downloadUrl: string;
}

/** Built-in MIME family filters. */
export type VaultPickerMimeFamily = 'image' | 'audio' | 'video' | 'document';

export interface VaultFilePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  /** Single-select callback. Fires when the user confirms a selection. */
  onFileSelected?: (file: VaultPickerResult) => void;
  /** Multi-select callback. Required when `multiSelect` is true. */
  onFilesSelected?: (files: VaultPickerResult[]) => void;

  /** Default: false. */
  multiSelect?: boolean;

  /** Dialog title — defaults to "Select File". */
  title?: string;
  /** Short description shown below the title. */
  description?: string;
  /** Confirm-button label. Defaults to "Open" / "Add N files". */
  confirmLabel?: string;

  /** Filter listed docs by a MIME family or a custom predicate. */
  mimeFilter?: VaultPickerMimeFamily | ((doc: VaultDocumentDto) => boolean);

  /** Show / hide the "Upload" button. Default: true. */
  allowUpload?: boolean;
  /** Restrict the upload `<input>`'s accept attribute. */
  uploadAccept?: string;
  /**
   * Let the Upload button take several files at once. Default: false.
   * Independent of `multiSelect`: picking many existing files and uploading
   * many new ones are separate capabilities.
   */
  multiUpload?: boolean;
  /** Per-file cap on uploads (bytes). Default: 50 MB. */
  maxUploadSize?: number;
  /** Optional pre-upload validator. Return a string to reject, null to allow. */
  validateUpload?: (file: File) => string | null;
  /** Optional cap on the selection. `0` disables choosing. */
  selectionLimit?: number;
  /** Shown when `selectionLimit` would be exceeded. */
  selectionLimitMessage?: string;
  /**
   * Browse this Holder, and only this one. Omitted: the user's Personal
   * Holder — what every consumer of this preset was built for. A
   * holder-scoped consumer (Fias Transfer: the server resolves documents
   * against the SPACE's holder) passes it so the user cannot pick files the
   * consumer would refuse. Surfaces that can take files from any workspace
   * use `OpenFileDialog` directly, which offers the switcher.
   */
  holderId?: string | null;
}

/** The kinds each legacy family covered. */
const FAMILY_KINDS: Record<VaultPickerMimeFamily, VaultFileKind[]> = {
  image: ['image'],
  audio: ['audio'],
  video: ['video'],
  document: ['pdf', 'document', 'spreadsheet', 'presentation', 'code'],
};

async function toPickerResult(doc: VaultFileDialogDocumentDto): Promise<VaultPickerResult> {
  const { url } = await api.getVaultDocumentDownloadUrl(doc.documentId);
  return {
    documentId: doc.documentId,
    holderId: doc.holderId,
    fileName: doc.name,
    mimeType: doc.mimeType,
    sizeBytes: doc.sizeBytes,
    s3Key: doc.s3Key,
    downloadUrl: url,
  };
}

function usePickerPreset(props: {
  mimeFilter?: VaultFilePickerDialogProps['mimeFilter'];
  multiSelect: boolean;
  onFileSelected?: (file: VaultPickerResult) => void;
  onFilesSelected?: (files: VaultPickerResult[]) => void;
}) {
  const { mimeFilter, multiSelect, onFileSelected, onFilesSelected } = props;
  const accept = useMemo<FileAccept | undefined>(
    () => (typeof mimeFilter === 'string' ? { kinds: FAMILY_KINDS[mimeFilter] } : undefined),
    [mimeFilter],
  );
  const filter = useMemo(
    () => (typeof mimeFilter === 'function' ? mimeFilter : undefined),
    [mimeFilter],
  );
  const onConfirm = useCallback(
    async (files: VaultFileDialogDocumentDto[]) => {
      const results = await Promise.all(files.map(toPickerResult));
      if (multiSelect && onFilesSelected) onFilesSelected(results);
      else onFileSelected?.(results[0]);
    },
    [multiSelect, onFileSelected, onFilesSelected],
  );
  return { accept, filter, onConfirm };
}

// ---------------------------------------------------------------------------
// Dialog — the canonical entry point.
// ---------------------------------------------------------------------------

export function VaultFilePickerDialog({
  open,
  onOpenChange,
  onFileSelected,
  onFilesSelected,
  multiSelect = false,
  title = 'Select File',
  description,
  confirmLabel,
  mimeFilter,
  allowUpload = true,
  uploadAccept,
  multiUpload = false,
  maxUploadSize,
  validateUpload,
  selectionLimit,
  selectionLimitMessage,
  holderId,
}: VaultFilePickerDialogProps) {
  const handleFile = useCallback(
    (file: VaultPickerResult) => {
      onFileSelected?.(file);
      onOpenChange(false);
    },
    [onFileSelected, onOpenChange],
  );
  const handleFiles = useCallback(
    (files: VaultPickerResult[]) => {
      onFilesSelected?.(files);
      onOpenChange(false);
    },
    [onFilesSelected, onOpenChange],
  );
  const preset = usePickerPreset({
    mimeFilter,
    multiSelect,
    onFileSelected: handleFile,
    onFilesSelected: handleFiles,
  });

  // A closed picker is not mounted: pages keep one around for later, and it
  // must not run its workspace and listing queries until it is opened.
  if (!open) return null;

  return (
    <OpenFileDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      multiSelect={multiSelect}
      confirmLabel={confirmLabel}
      accept={preset.accept}
      filter={preset.filter}
      allowUpload={allowUpload}
      uploadAccept={uploadAccept}
      multiUpload={multiUpload}
      maxUploadSize={maxUploadSize}
      validateUpload={validateUpload}
      selectionLimit={selectionLimit}
      selectionLimitMessage={selectionLimitMessage}
      initialHolderId={holderId ?? null}
      lockHolder={!!holderId}
      personalOnly={!holderId}
      memoryKey="vault-file-picker"
      onConfirm={preset.onConfirm}
    />
  );
}

// ---------------------------------------------------------------------------
// Panel — the same picker inline, for a surface's own multi-tab modal
// (Social Media's MediaPickerModal).
// ---------------------------------------------------------------------------

export interface VaultFilePickerPanelProps {
  isActive: boolean;
  onFileSelected?: (file: VaultPickerResult) => void;
  onFilesSelected?: (files: VaultPickerResult[]) => void;
  multiSelect?: boolean;
  confirmLabel?: string;
  mimeFilter?: VaultFilePickerDialogProps['mimeFilter'];
  allowUpload?: boolean;
  uploadAccept?: string;
  multiUpload?: boolean;
  maxUploadSize?: number;
  validateUpload?: VaultFilePickerDialogProps['validateUpload'];
  selectionLimit?: number;
  selectionLimitMessage?: string;
  holderId?: string | null;
  /** Kept for API compatibility; the panel fills its container. */
  className?: string;
}

export function VaultFilePickerPanel({
  isActive,
  onFileSelected,
  onFilesSelected,
  multiSelect = false,
  confirmLabel,
  mimeFilter,
  allowUpload = true,
  uploadAccept,
  multiUpload = false,
  maxUploadSize,
  validateUpload,
  selectionLimit,
  selectionLimitMessage,
  holderId,
}: VaultFilePickerPanelProps) {
  const preset = usePickerPreset({ mimeFilter, multiSelect, onFileSelected, onFilesSelected });
  if (!isActive) return null;
  return (
    <OpenFileDialog
      open={isActive}
      onOpenChange={() => {}}
      embedded
      multiSelect={multiSelect}
      confirmLabel={confirmLabel}
      accept={preset.accept}
      filter={preset.filter}
      allowUpload={allowUpload}
      uploadAccept={uploadAccept}
      multiUpload={multiUpload}
      maxUploadSize={maxUploadSize}
      validateUpload={validateUpload}
      selectionLimit={selectionLimit}
      selectionLimitMessage={selectionLimitMessage}
      initialHolderId={holderId ?? null}
      lockHolder={!!holderId}
      personalOnly={!holderId}
      memoryKey="vault-file-picker"
      onConfirm={preset.onConfirm}
    />
  );
}

export default VaultFilePickerDialog;
